import * as React from "react";
import PropTypes from "prop-types";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Typography,
    Chip,
} from "@mui/material";
import { ExpandMore, FlightTakeoff, FlightLand } from "@mui/icons-material";
import CardVuelo from "./cardVuelo.jsx";

/**
 * ListaVuelosAeropuerto
 * Muestra un aeropuerto con su lista de vuelos dentro de un Accordion estilizado.
 *
 * Props:
 * - airport: { id, name, fullName }
 * - flights: array de vuelos del aeropuerto
 * - expandedId: id del aeropuerto actualmente expandido
 * - onToggle: (airportId: string|number, isExpanded: boolean) => void
 * - onDownload: (flightId: string|number) => void  // se pasa a CardVuelo
 * - sx: estilos adicionales para el Accordion
 */
export default function ListaVuelosAeropuerto({
    airport,
    flights,
    expandedId,
    onToggle,
    onDownload,
    sx,
}) {
    const isExpanded = expandedId === airport.id;
    const flightsByDate = (flights ?? []).reduce((acc, flight) => {
        const dateKey = flight?.fechaSalida || "Sin fecha";
        if (!acc[dateKey]) {
            acc[dateKey] = [];
        }
        acc[dateKey].push(flight);
        return acc;
    }, {});
    const dateKeys = Object.keys(flightsByDate);
    const shouldGroupByDate = dateKeys.length > 1;

    const handleChange = (_e, newExpanded) => {
        onToggle?.(airport.id, newExpanded);
    };

    return (
        <Accordion
            expanded={isExpanded}
            onChange={handleChange}
            sx={{
                mb: 2,
                borderRadius: 2,
                "&:before": { display: "none" },
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                ...sx,
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                    bgcolor: isExpanded ? "#e0f7fa" : "white",
                    "&:hover": { bgcolor: "#f5f5f5" },
                    borderRadius: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: { xs: "flex-start", sm: "center" },
                        gap: 2,
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                        width: "100%",
                        minWidth: 0,
                    }}
                >
                    <FlightTakeoff
                        sx={{ color: "#3baec7", fontSize: 28, flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, color: "#3baec7" }}
                        >
                            {airport.name}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            {airport.fullName}
                        </Typography>
                    </Box>
                    <Chip
                        label={`${flights?.length ?? 0} vuelos`}
                        size="small"
                        sx={{
                            bgcolor: "#3baec7",
                            color: "white",
                            flexShrink: 0,
                            alignSelf: { xs: "flex-start", sm: "center" },
                        }}
                    />
                </Box>
            </AccordionSummary>

            <AccordionDetails
                sx={{
                    bgcolor: "#fafafa",
                    p: 3,
                    maxHeight: { xs: "50vh", md: "45vh" },
                    overflowY: "auto",
                }}
            >
                {(flights?.length ?? 0) === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <FlightLand
                            sx={{ fontSize: 48, color: "#bdbdbd", mb: 2 }}
                        />
                        <Typography variant="body1" color="text.secondary">
                            No hay vuelos programados para este aeropuerto en
                            este momento.
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        {shouldGroupByDate
                            ? dateKeys.map((dateKey) => (
                                  <Box
                                      key={dateKey}
                                      sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 2,
                                      }}
                                  >
                                      <Typography
                                          variant="subtitle2"
                                          sx={{
                                              fontWeight: 700,
                                              color: "#3baec7",
                                          }}
                                      >
                                          {dateKey}
                                      </Typography>
                                      {flightsByDate[dateKey].map((flight) => (
                                          <CardVuelo
                                              key={flight.id}
                                              flight={flight}
                                              onDownload={onDownload}
                                          />
                                      ))}
                                  </Box>
                              ))
                            : flights.map((flight) => (
                                  <CardVuelo
                                      key={flight.id}
                                      flight={flight}
                                      onDownload={onDownload}
                                  />
                              ))}
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
    );
}

ListaVuelosAeropuerto.propTypes = {
    airport: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
        name: PropTypes.string.isRequired,
        fullName: PropTypes.string,
    }).isRequired,
    flights: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            destino: PropTypes.string.isRequired,
            horaSalida: PropTypes.string.isRequired,
            fechaSalida: PropTypes.string,
        })
    ).isRequired,
    expandedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onToggle: PropTypes.func, // (airportId, isExpanded) => void
    onDownload: PropTypes.func, // (flightId) => void (se forwards a CardVuelo)
    sx: PropTypes.object,
};
